/**
 * E018-S012-T001: BlockDiscoveryService
 *
 * Abstrakte Basis-Klasse für workspace-weites Block-Discovery.
 * Ermöglicht DRY-Implementierung für Database- und Automation-Discovery.
 *
 * Subklassen:
 * - DatabaseDiscoveryService (affine:database)
 * - AutomationDiscoveryService (affine:automation)
 *
 * Features:
 * - Reactive Streams (blocks$, canvasBlocks$, blocksByDoc$, canvasBlocksByDoc$)
 * - Working-set scanning via DocsService.loadedDocIds$
 * - Scans only after doc content load readiness, so empty pre-sync docs do not
 *   poison the workspace index.
 * - Canvas-Filter (xywh vorhanden)
 * - Doc-Gruppierung
 * - Automatic doc subscription for reactivity
 * - Delayed retry for docs not yet loaded
 */

import type { Block, Store } from '@blocksuite/affine/store';
import { LiveData, Service } from '@toeverything/infra';
import {
  BehaviorSubject,
  combineLatest,
  debounceTime,
  distinctUntilChanged,
  startWith,
  switchMap,
} from 'rxjs';

import type { DocRecord, DocsService } from '../../doc';

/**
 * Base interface for block info.
 * Subclasses extend this with block-specific fields.
 */
export interface BlockInfo {
  /** Block ID */
  blockId: string;
  /** Doc ID containing this block */
  docId: string;
  /** Doc title (Page Name) */
  docTitle: string;
  /** Canvas position. Undefined = In-Note block */
  xywh?: string;
  /** Whether the block is in trash */
  isInTrash?: boolean;
}

/**
 * Doc group for UI display.
 */
export interface DocGroup<T extends BlockInfo> {
  docId: string;
  docTitle: string;
  blocks: T[];
}

/**
 * Trashcan item structure from affine:trashcan block.
 */
export interface TrashcanItem {
  id: string;
  blockType: string;
  snapshot?: unknown;
  displayName?: string;
  deletedAt?: number;
  /** Sharded database identity persisted at trash time (database items). */
  databaseId?: string;
  databaseRootDocId?: string;
}

/**
 * Abstract base class for workspace-wide block discovery.
 *
 * @typeParam T - The block info type (extends BlockInfo)
 */
export abstract class BlockDiscoveryService<
  T extends BlockInfo,
> extends Service {
  constructor(protected readonly docsService: DocsService) {
    super();
    if (typeof window !== 'undefined') {
      window.addEventListener(
        'streamlain:block-trashed',
        this.handleTrashcanMutation
      );
      window.addEventListener(
        'streamlain:block-restored',
        this.handleTrashcanMutation
      );
      window.addEventListener(
        'streamlain:block-permanently-deleted',
        this.handleTrashcanMutation
      );
    }
  }

  /**
   * Trigger for manual or automatic refreshes.
   * Emits on block changes in any doc.
   */
  protected readonly refreshTrigger$ = new BehaviorSubject<number>(0);

  /**
   * Active subscriptions to doc block updates.
   * Map of docId to unsubscribe function.
   */
  protected readonly docSubscriptions = new Map<string, () => void>();

  /**
   * Set of doc IDs that need a retry because they weren't loaded yet.
   */
  protected readonly pendingRetryDocs = new Set<string>();

  private readonly handleTrashcanMutation = (event: Event): void => {
    if (!this.trashcanBlockType) return;
    const detail = (event as CustomEvent<{ blockType?: string }>).detail;
    if (!detail?.blockType || detail.blockType === this.trashcanBlockType) {
      this.refreshTrigger$.next(Date.now());
    }
  };

  // ============================================================
  // Abstract Properties & Methods (implemented by subclasses)
  // ============================================================

  /**
   * Block flavour to scan for (e.g., 'affine:database', 'affine:automation').
   */
  abstract readonly blockFlavour: string;

  /**
   * Trashcan block type for this block flavour (e.g., 'database', 'automation').
   * Return null if trashcan scanning is not needed.
   */
  abstract readonly trashcanBlockType: string | null;

  /**
   * Extract block info from a BlockSuite block.
   * Return null to skip this block.
   */
  abstract extractBlockInfo(block: Block, docRecord: DocRecord): T | null;

  /**
   * Extract block info from a trashcan item.
   * Return null to skip this item.
   * Optional: only needed if trashcanBlockType is set.
   *
   * @param hostStore - Store of the document owning the trashcan; lets
   *   implementations resolve workspace-level storage documents.
   */
  extractTrashcanInfo?(
    item: TrashcanItem,
    docRecord: DocRecord,
    hostStore: Store
  ): T | null | Promise<T | null>;

  protected reconcileTrashcanItems?(
    items: TrashcanItem[],
    hostStore: Store
  ): void;

  // ============================================================
  // Protected Helper Methods
  // ============================================================

  /**
   * Schedule a retry for a doc that isn't loaded yet.
   * Uses short delay (150ms) for good UX with newly created pages.
   */
  protected scheduleRetryForDoc(docId: string): void {
    if (this.pendingRetryDocs.has(docId)) return;

    this.pendingRetryDocs.add(docId);
    setTimeout(() => {
      this.pendingRetryDocs.delete(docId);
      this.refreshTrigger$.next(Date.now());
    }, 150);
  }

  /**
   * Subscribes to blockUpdated events for a doc.
   * Triggers refresh when blocks of our flavour are added/deleted/updated.
   */
  protected subscribeToDoc(docId: string): void {
    if (this.docSubscriptions.has(docId)) return;

    try {
      const loaded = this.docsService.loaded(docId);
      if (!loaded) return;
      const { doc, release } = loaded;
      const blockSuiteDoc = doc.blockSuiteDoc;

      if (!blockSuiteDoc?.slots?.blockUpdated) {
        release();
        this.scheduleRetryForDoc(docId);
        return;
      }

      // Subscribe to block updates.
      // Only react to changes of OUR flavour (and the trashcan that mirrors it).
      // Reacting to generic add/delete of ANY flavour caused a self-sustaining
      // rescan loop: a scan opens/loads docs, which re-emits add events, which
      // re-triggered the scan. Narrowing the filter keeps reactivity for real
      // database/automation changes while idling when nothing relevant changes.
      const blockDisposable = blockSuiteDoc.slots.blockUpdated.subscribe(
        (payload: { type: string; flavour?: string }) => {
          if (
            payload.flavour === this.blockFlavour ||
            payload.flavour === 'affine:trashcan'
          ) {
            this.refreshTrigger$.next(Date.now());
          }
        }
      );

      // Subscribe to doc title changes for reactive sidebar updates
      const titleDisposable = doc.title$.subscribe(() => {
        this.refreshTrigger$.next(Date.now());
      });

      // Do not retain the DocScope. Its owner controls working-set lifetime;
      // loadedDocIds$ removes this subscription when the scope is collected.
      this.docSubscriptions.set(docId, () => {
        blockDisposable.unsubscribe();
        titleDisposable.unsubscribe();
      });
      release();
    } catch {
      this.scheduleRetryForDoc(docId);
    }
  }

  /**
   * Unsubscribes from a doc's block updates.
   */
  protected unsubscribeFromDoc(docId: string): void {
    const cleanup = this.docSubscriptions.get(docId);
    if (cleanup) {
      cleanup();
      this.docSubscriptions.delete(docId);
    }
  }

  // ============================================================
  // Reactive Streams
  // ============================================================

  /**
   * LiveData with blocks from the explicitly loaded workspace working set.
   * Reactive: Updates on loaded-doc changes and relevant block changes.
   */
  readonly blockCatalog$ = LiveData.from<{
    blocks: T[];
    scannedDocIds: string[];
  }>(
    combineLatest([
      this.docsService.loadedDocIds$,
      this.refreshTrigger$.pipe(startWith(0), debounceTime(100)),
    ]).pipe(
      switchMap(async ([loadedDocIds]: [readonly string[], number]) => {
        const blocks: T[] = [];
        const scannedDocIds: string[] = [];
        // Opening a document is an authoritative content-sync request. Block
        // discovery may inspect the explicit working set, but must never turn
        // a metadata page into N document opens. System docs do not contain
        // editor blocks of these flavours.
        const docs = loadedDocIds.flatMap(docId => {
          if (docId.includes('$')) return [];
          const record = this.docsService.list.doc$(docId).value;
          return record ? [record] : [];
        });
        const currentDocIds = new Set(docs.map(d => d.id));

        // Cleanup subscriptions for removed docs
        for (const docId of this.docSubscriptions.keys()) {
          if (!currentDocIds.has(docId)) {
            this.unsubscribeFromDoc(docId);
          }
        }

        for (const docRecord of docs) {
          try {
            // Subscribe to this doc's block updates (idempotent)
            this.subscribeToDoc(docRecord.id);

            const loaded = this.docsService.loaded(docRecord.id);
            if (!loaded) continue;
            const { doc, release } = loaded;

            try {
              const blockSuiteDoc = doc.blockSuiteDoc;
              if (!blockSuiteDoc) {
                this.scheduleRetryForDoc(docRecord.id);
                continue;
              }

              // The document was explicitly opened by its owner. Await the
              // existing sync job, but never create one from discovery.
              await doc.waitForSyncReady();

              // Find all blocks of our flavour
              const foundBlocks = blockSuiteDoc.getBlocksByFlavour(
                this.blockFlavour
              );

              for (const block of foundBlocks) {
                const blockInfo = this.extractBlockInfo(block, docRecord);
                if (blockInfo) {
                  blocks.push(blockInfo);
                }
              }

              // Scan trashcan if configured
              if (this.trashcanBlockType && this.extractTrashcanInfo) {
                const trashcan =
                  blockSuiteDoc.getBlocksByFlavour('affine:trashcan')[0];
                this.reconcileTrashcanItems?.(
                  (
                    trashcan?.model as
                      | { props?: { items?: TrashcanItem[] } }
                      | undefined
                  )?.props?.items ?? [],
                  blockSuiteDoc
                );
                if (trashcan) {
                  const trashcanModel = trashcan.model as {
                    props?: {
                      items?: TrashcanItem[];
                    };
                  };
                  const items = trashcanModel?.props?.items ?? [];
                  for (const item of items) {
                    if (item.blockType === this.trashcanBlockType) {
                      const trashedInfo = await this.extractTrashcanInfo(
                        item,
                        docRecord,
                        blockSuiteDoc
                      );
                      if (trashedInfo) {
                        blocks.push(trashedInfo);
                      }
                    }
                  }
                }
              }
              scannedDocIds.push(docRecord.id);
            } finally {
              release();
            }
          } catch {
            this.scheduleRetryForDoc(docRecord.id);
          }
        }

        return { blocks, scannedDocIds };
      }),
      // Collapse identical scan results so a refresh that produced no real
      // change does not push churn through the derived streams / React.
      // JSON equality is conservative: any real field change differs.
      distinctUntilChanged((a, b) => JSON.stringify(a) === JSON.stringify(b))
    ),
    { blocks: [], scannedDocIds: [] }
  );

  readonly blocks$ = this.blockCatalog$.map(catalog => catalog.blocks);

  /**
   * Blocks grouped by doc for UI display.
   */
  readonly blocksByDoc$ = this.blocks$.map((blocks: T[]) => {
    const byDoc = new Map<string, { docTitle: string; blocks: T[] }>();

    for (const block of blocks) {
      if (!byDoc.has(block.docId)) {
        byDoc.set(block.docId, {
          docTitle: block.docTitle,
          blocks: [],
        });
      }
      byDoc.get(block.docId)!.blocks.push(block);
    }

    return Array.from(byDoc.entries()).map(
      ([docId, data]): DocGroup<T> => ({
        docId,
        docTitle: data.docTitle,
        blocks: data.blocks,
      })
    );
  });

  /**
   * Canvas-only blocks (filtered by xywh presence).
   * In-Note blocks (without xywh) are excluded.
   */
  readonly canvasBlocks$ = this.blocks$.map((blocks: T[]) =>
    blocks.filter(block => block.xywh && !block.isInTrash)
  );

  /**
   * Canvas blocks grouped by doc.
   */
  readonly canvasBlocksByDoc$ = this.canvasBlocks$.map((blocks: T[]) => {
    const byDoc = new Map<string, { docTitle: string; blocks: T[] }>();

    for (const block of blocks) {
      if (!byDoc.has(block.docId)) {
        byDoc.set(block.docId, {
          docTitle: block.docTitle,
          blocks: [],
        });
      }
      byDoc.get(block.docId)!.blocks.push(block);
    }

    return Array.from(byDoc.entries()).map(
      ([docId, data]): DocGroup<T> => ({
        docId,
        docTitle: data.docTitle,
        blocks: data.blocks,
      })
    );
  });

  // ============================================================
  // Public Methods
  // ============================================================

  /**
   * Find a block by its ID.
   */
  findBlock(blockId: string): T | undefined {
    return this.blocks$.value.find((block: T) => block.blockId === blockId);
  }

  /**
   * Manual refresh trigger.
   */
  refresh(): void {
    this.refreshTrigger$.next(Date.now());
  }

  /**
   * Cleanup when service is disposed.
   */
  override dispose(): void {
    if (typeof window !== 'undefined') {
      window.removeEventListener(
        'streamlain:block-trashed',
        this.handleTrashcanMutation
      );
      window.removeEventListener(
        'streamlain:block-restored',
        this.handleTrashcanMutation
      );
      window.removeEventListener(
        'streamlain:block-permanently-deleted',
        this.handleTrashcanMutation
      );
    }
    for (const docId of this.docSubscriptions.keys()) {
      this.unsubscribeFromDoc(docId);
    }
    super.dispose();
  }
}
